<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.sales_entry.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row align-items-center">
            <div class="col">
              <h3 class="mb-0">{{ $t("modern_trade.sales_entry.title") }}</h3>
            </div>
            <div class="col text-right" v-if="isEditModernTrade">
              <base-button size="sm" type="default" @click="openImport">
                <i class="fa fa-file-upload"></i> {{ $t("modern_trade.sales_entry.btn_import") }}
              </base-button>
              <base-button size="sm" type="primary" @click="openAdd">
                <i class="fa fa-plus"></i> {{ $t("modern_trade.sales_entry.btn_add") }}
              </base-button>
            </div>
          </div>
        </template>

        <!-- Filters -->
        <div class="row align-items-end mb-3 px-4">
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.sales_entry.company") }}</label>
            <el-select
              v-model="filters.company_id"
              :placeholder="$t('all') || 'All'"
              clearable
              class="w-100"
              @change="onFilter"
            >
              <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id"></el-option>
            </el-select>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.sales_entry.date_from") }}</label>
            <el-date-picker
              v-model="filters.date_from"
              type="date"
              value-format="yyyy-MM-dd"
              clearable
              class="w-100"
              @change="onFilter"
            ></el-date-picker>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.sales_entry.date_to") }}</label>
            <el-date-picker
              v-model="filters.date_to"
              type="date"
              value-format="yyyy-MM-dd"
              clearable
              class="w-100"
              @change="onFilter"
            ></el-date-picker>
          </div>
          <div class="col-md-5">
            <label class="form-control-label">{{ $t("search") || "Search" }}</label>
            <div class="d-flex">
              <el-input
                v-model="filters.search"
                :placeholder="$t('modern_trade.sales_entry.search_placeholder')"
                clearable
                class="flex-fill mr-2"
                @keyup.enter.native="onFilter"
                @clear="onFilter"
              ></el-input>
              <base-button type="primary" @click="onFilter">
                <i class="fa fa-search"></i>
              </base-button>
            </div>
          </div>
        </div>

        <!-- List -->
        <div v-loading="loading" class="px-4">
          <el-table :data="rows" row-key="id" header-row-class-name="thead-light" size="small">
            <el-table-column :label="$t('modern_trade.sales_entry.doc_no')" prop="doc_no" min-width="140"></el-table-column>
            <el-table-column :label="$t('modern_trade.sales_entry.company')" min-width="150">
              <template slot-scope="{ row }">{{ row.company_name || row.company_id || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sales_entry.branch')" prop="branch_code" min-width="80" align="center"></el-table-column>
            <el-table-column :label="$t('modern_trade.sales_entry.sold_at')" min-width="110">
              <template slot-scope="{ row }">{{ formatDate(row.sold_at, false) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sales_entry.item_count')" prop="item_count" min-width="90" align="center"></el-table-column>
            <el-table-column :label="$t('modern_trade.sales_entry.total_qty')" prop="total_qty" min-width="90" align="center"></el-table-column>
            <el-table-column :label="$t('modern_trade.sales_entry.source')" min-width="130" align="center">
              <template slot-scope="{ row }">
                <span class="badge badge-pill source-badge" :class="sourceBadgeClass(row.source)">
                  {{ sourceLabel(row.source) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sales_entry.created_at')" min-width="140">
              <template slot-scope="{ row }">{{ formatDate(row.created_at, true) }}</template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="90" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="default" @click.native="openDetail(row)">
                  <i class="fa fa-eye"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadList"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- ═══ Detail modal ═══ -->
    <el-dialog
      :title="$t('modern_trade.sales_entry.detail_title')"
      :visible.sync="detailVisible"
      width="820px"
      append-to-body
    >
      <div v-loading="detailLoading" v-if="detail">
        <dl class="row mb-3">
          <dt class="col-3 text-muted">{{ $t("modern_trade.sales_entry.doc_no") }}</dt>
          <dd class="col-3"><strong>{{ detail.doc_no || "-" }}</strong></dd>
          <dt class="col-3 text-muted">{{ $t("modern_trade.sales_entry.company") }}</dt>
          <dd class="col-3">{{ detail.company_name || detail.company_id || "-" }}</dd>

          <dt class="col-3 text-muted">{{ $t("modern_trade.sales_entry.branch") }}</dt>
          <dd class="col-3">{{ detail.branch_code || "-" }}</dd>
          <dt class="col-3 text-muted">{{ $t("modern_trade.sales_entry.sold_at") }}</dt>
          <dd class="col-3">{{ formatDate(detail.sold_at, false) }}</dd>

          <dt class="col-3 text-muted">{{ $t("modern_trade.sales_entry.source") }}</dt>
          <dd class="col-3">
            <span class="badge badge-pill source-badge" :class="sourceBadgeClass(detail.source)">
              {{ sourceLabel(detail.source) }}
            </span>
          </dd>
          <dt class="col-3 text-muted">{{ $t("modern_trade.sales_entry.created_at") }}</dt>
          <dd class="col-3">{{ formatDate(detail.created_at, true) }}</dd>
        </dl>

        <strong class="text-muted small">{{ $t("modern_trade.sales_entry.detail_items") }}</strong>
        <el-table :data="detail.items || []" header-row-class-name="thead-light" size="small" class="mt-1">
          <el-table-column type="expand">
            <template slot-scope="{ row }">
              <div class="px-4 py-2">
                <strong class="text-muted small">{{ $t("modern_trade.sales_entry.deducted_lots") }}</strong>
                <table v-if="row.deducted && row.deducted.length" class="table table-sm table-bordered mt-1" style="width:auto">
                  <thead>
                    <tr>
                      <th>{{ $t("modern_trade.sales_entry.lot_no") }}</th>
                      <th>{{ $t("modern_trade.sales_entry.qty") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(d, i) in row.deducted" :key="i">
                      <td>{{ d.lot_no || "-" }}</td>
                      <td>{{ d.qty }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="text-muted small mt-1">{{ $t("mt_shared.no_data") }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.sku_code')" prop="sku_code" min-width="110"></el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.barcode')" prop="barcode" min-width="120"></el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.product_name')" min-width="180">
            <template slot-scope="{ row }">
              <a href="javascript:void(0)" @click="showProduct(row)">{{ row.product_name || row.product_code || "-" }}</a>
            </template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.lot_no')" min-width="110">
            <template slot-scope="{ row }">{{ row.lot_no || $t("modern_trade.sales_entry.lot_auto_fefo") }}</template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.qty')" prop="qty" min-width="70" align="right"></el-table-column>
        </el-table>
      </div>
      <span slot="footer">
        <base-button type="secondary" @click="detailVisible = false">{{ $t("mt_shared.close") }}</base-button>
      </span>
    </el-dialog>

    <!-- ═══ Import modal ═══ -->
    <el-dialog
      :title="$t('modern_trade.sales_entry.import_title')"
      :visible.sync="importVisible"
      width="960px"
      append-to-body
      @closed="onImportClosed"
    >
      <!-- Company + branch header (always) -->
      <div class="row align-items-end mb-3">
        <div class="col-md-5">
          <label class="form-control-label">{{ $t("modern_trade.sales_entry.pick_company") }} *</label>
          <el-select
            v-model="importForm.company_id"
            :placeholder="$t('modern_trade.sales_entry.pick_company')"
            filterable
            class="w-100"
            :disabled="importStep === 2"
          >
            <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </div>
        <div class="col-md-3">
          <label class="form-control-label">{{ $t("modern_trade.sales_entry.branch_code") }}</label>
          <el-input v-model="importForm.branch_code" :disabled="importStep === 2"></el-input>
        </div>
      </div>

      <!-- Step 1: pick file -->
      <div v-if="importStep === 1">
        <div class="row align-items-end">
          <div class="col-md-7">
            <label class="form-control-label">{{ $t("modern_trade.import.pick_file") }} (.xls / .xlsx)</label>
            <input
              ref="importFileInput"
              type="file"
              accept=".xls,.xlsx"
              class="form-control"
              @change="onFileChange"
            />
          </div>
          <div class="col-md-5 pb-1">
            <base-button type="default" @click="downloadTemplate">
              <i class="fa fa-download"></i> {{ $t("modern_trade.import.template") }}
            </base-button>
          </div>
        </div>

        <!-- File format guide (#155: sku_code / barcode only — no product code) -->
        <div class="card bg-secondary shadow-none mt-3 mb-0">
          <div class="card-body py-3">
            <h5 class="mb-2">
              <i class="fa fa-info-circle text-info mr-1"></i>
              {{ $t("modern_trade.sales_entry.file_format_title") }}
            </h5>
            <ul class="format-hint mb-2">
              <li>{{ $t("modern_trade.sales_entry.file_format_hint_key") }}</li>
              <li>{{ $t("modern_trade.sales_entry.file_format_hint_date") }}</li>
              <li>{{ $t("modern_trade.sales_entry.file_format_hint_lot") }}</li>
              <li>{{ $t("modern_trade.sales_entry.file_format_hint_qty") }}</li>
            </ul>
            <div class="table-responsive">
              <table class="table table-sm table-bordered bg-white mb-0 format-sample">
                <thead class="thead-light">
                  <tr>
                    <th v-for="h in templateHeaders" :key="h">{{ h }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(r, i) in templateSampleRows" :key="i">
                    <td v-for="(c, j) in r" :key="j">{{ c || "-" }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: preview -->
      <div v-if="importStep === 2">
        <div class="mb-3">
          <span class="badge badge-secondary mr-2">{{ $t("modern_trade.import.summary_total") }}: {{ batch.row_total }}</span>
          <span class="badge badge-success mr-2">{{ $t("modern_trade.import.summary_ok") }}: {{ batch.row_ok }}</span>
          <span class="badge badge-danger mr-2">{{ $t("modern_trade.import.summary_error") }}: {{ batch.row_error }}</span>
        </div>

        <div v-if="batch.row_error > 0" class="alert alert-danger">
          <i class="fa fa-exclamation-triangle mr-2"></i>
          {{ $t("modern_trade.validation.all_or_nothing_blocked") }}
        </div>

        <!-- BE caps the error list at mtImportRowsCap (500) rows even when
             row_error is larger — say so, or the badge count silently
             disagrees with what's on screen and the user thinks they fixed
             everything. -->
        <div v-if="previewRows.length < batch.row_error" class="alert alert-warning">
          <i class="fa fa-info-circle mr-2"></i>
          {{ $t("modern_trade.sales_entry.import_errors_truncated", { shown: previewRows.length, total: batch.row_error }) }}
        </div>

        <el-table :data="previewRows" header-row-class-name="thead-light" size="small" row-key="row_no" max-height="360">
          <el-table-column :label="$t('modern_trade.import.row_no')" prop="row_no" width="60" align="center"></el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.sold_at')" min-width="110">
            <template slot-scope="{ row }">{{ cellText(row.sale_date) }}</template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.sku_code')" min-width="110">
            <template slot-scope="{ row }">{{ cellText(row.sku_code) }}</template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.barcode')" min-width="120">
            <template slot-scope="{ row }">{{ cellText(row.barcode) }}</template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.product_name')" min-width="150">
            <template slot-scope="{ row }">{{ row.product_name || row.sku_code || row.barcode || "-" }}</template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.lot_no')" min-width="100">
            <template slot-scope="{ row }">{{ lotCellText(row) }}</template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.sales_entry.qty')" min-width="70" align="right">
            <template slot-scope="{ row }">{{ cellText(row.qty) }}</template>
          </el-table-column>
          <el-table-column :label="$t('status')" min-width="80" align="center">
            <template slot-scope="{ row }">
              <span class="badge" :class="row.status === 1 ? 'badge-success' : 'badge-danger'">
                {{ row.status === 1 ? $t("modern_trade.import.status_ok") : $t("modern_trade.import.status_error") }}
              </span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('modern_trade.import.error_message')" min-width="200">
            <template slot-scope="{ row }">
              <span v-if="row.error_msg" class="text-danger small">{{ translateError(row.error_msg) }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <span slot="footer">
        <base-button type="secondary" @click="importVisible = false">{{ $t("mt_shared.close") }}</base-button>
        <base-button
          v-if="importStep === 1"
          type="primary"
          :loading="validating"
          :disabled="!importFile || !importForm.company_id"
          @click="doValidate"
        >
          <i class="fa fa-check"></i> {{ $t("modern_trade.import.validate") }}
        </base-button>
        <template v-if="importStep === 2">
          <base-button type="default" @click="backToImportStep1">
            <i class="fa fa-arrow-left"></i> {{ $t("modern_trade.import.cancel") }}
          </base-button>
          <base-button
            type="success"
            :loading="confirming"
            :disabled="batch.row_error > 0 || batch.row_ok === 0"
            @click="doConfirm"
          >
            <i class="fa fa-check"></i> {{ $t("modern_trade.import.confirm") }}
          </base-button>
        </template>
      </span>
    </el-dialog>

    <!-- ═══ Add (manual entry) modal ═══ -->
    <el-dialog
      :title="$t('modern_trade.sales_entry.add_title')"
      :visible.sync="addVisible"
      width="960px"
      append-to-body
      @closed="onAddClosed"
    >
      <div class="row align-items-end mb-3">
        <div class="col-md-5">
          <label class="form-control-label">{{ $t("modern_trade.sales_entry.pick_company") }} *</label>
          <el-select
            v-model="addForm.company_id"
            :placeholder="$t('modern_trade.sales_entry.pick_company')"
            filterable
            class="w-100"
          >
            <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </div>
        <div class="col-md-3">
          <label class="form-control-label">{{ $t("modern_trade.sales_entry.sold_at") }} *</label>
          <el-date-picker
            v-model="addForm.sold_at"
            type="date"
            value-format="yyyy-MM-dd"
            class="w-100"
          ></el-date-picker>
        </div>
        <div class="col-md-4">
          <label class="form-control-label">{{ $t("modern_trade.sales_entry.branch_code") }}</label>
          <el-input v-model="addForm.branch_code"></el-input>
        </div>
      </div>

      <el-table :data="addForm.items" header-row-class-name="thead-light" size="small">
        <el-table-column :label="$t('modern_trade.sales_entry.select_product')" min-width="240">
          <template slot-scope="{ row }">
            <el-select
              v-model="row.product_id"
              filterable
              remote
              :remote-method="(q) => searchProducts(q, row)"
              :loading="row.productLoading"
              :placeholder="$t('modern_trade.sales_entry.select_product')"
              class="w-100"
              @change="onProductChange(row)"
              @focus="searchProducts('', row)"
            >
              <el-option
                v-for="p in row.productOptions"
                :key="p.id"
                :label="productLabel(p)"
                :value="p.id"
              ></el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$t('modern_trade.sales_entry.lot_no')" min-width="240">
          <template slot-scope="{ row }">
            <el-select
              v-model="row.lot_no"
              clearable
              :placeholder="$t('modern_trade.sales_entry.lot_auto_fefo')"
              :loading="row.lotLoading"
              :disabled="!row.product_id"
              class="w-100"
            >
              <el-option :label="$t('modern_trade.sales_entry.lot_auto_fefo')" :value="''"></el-option>
              <el-option
                v-for="lot in row.lotOptions"
                :key="lot.lot_no"
                :label="lotLabel(lot)"
                :value="lot.lot_no"
              ></el-option>
            </el-select>
          </template>
        </el-table-column>
        <el-table-column :label="$t('modern_trade.sales_entry.qty')" width="140" align="center">
          <template slot-scope="{ row }">
            <el-input-number v-model="row.qty" :min="1" :controls="false" size="small" class="w-100"></el-input-number>
          </template>
        </el-table-column>
        <el-table-column width="70" align="center">
          <template slot-scope="{ $index }">
            <base-button size="sm" type="danger" @click.native="removeItem($index)">
              <i class="fa fa-trash"></i>
            </base-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="mt-2">
        <base-button size="sm" type="default" @click="addItem">
          <i class="fa fa-plus"></i> {{ $t("modern_trade.sales_entry.add_product") }}
        </base-button>
      </div>

      <span slot="footer">
        <base-button type="secondary" @click="addVisible = false">{{ $t("mt_shared.close") }}</base-button>
        <base-button type="success" :loading="saving" @click="doSaveManual">
          <i class="fa fa-check"></i> {{ $t("modern_trade.sales_entry.save") }}
        </base-button>
      </span>
    </el-dialog>

    <product-detail-popup :visible.sync="productPopupVisible" :product="productPopupData"></product-detail-popup>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, DatePicker, Dialog, Input, InputNumber } from "element-ui";
import { utils, writeFile } from "xlsx";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import ProductDetailPopup from "@/components/Modern/ProductDetailPopup";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

const DEFAULT_BRANCH = "00000";

// #155 / bo-api#306 — import file contract: match products by sku_code (man_pn)
// or barcode ONLY. product_code is no longer supported.
const TEMPLATE_COLUMNS = ["sku_code", "barcode", "sale_date", "lot_no", "qty"];
const TEMPLATE_SAMPLE_ROWS = [
  ["SKU001", "", "2025-01-15", "", "50"],
  ["", "8850001234567", "2025-01-15", "LOT-2501", "12"],
];

export default {
  name: "MtSalesImport",
  components: {
    RouteBreadCrumb,
    BasePagination,
    ProductDetailPopup,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Input.name]: Input,
    [InputNumber.name]: InputNumber,
  },
  computed: {
    ...mapGetters({
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    // Header row shown in the on-screen guide AND written to the xlsx template,
    // so the two can never drift apart.
    templateHeaders() {
      return TEMPLATE_COLUMNS.map((c) => `${c} (${this.$t(`modern_trade.sales_entry.col_${c}`)})`);
    },
    templateSampleRows() {
      return TEMPLATE_SAMPLE_ROWS;
    },
    // Step 2 is fed from two different BE shapes: the 200 validate response
    // (a real batch was created → batch.id set) or the 400 validation_failed
    // payload (error rows only, no batch). In the latter case nothing was
    // resolved, so we must not pretend an empty lot means "auto FEFO".
    previewErrorMode() {
      return this.importStep === 2 && !this.batch.id;
    },
  },
  data() {
    return {
      // list
      loading: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      filters: { company_id: null, date_from: null, date_to: null, search: "" },
      companies: [],
      // detail
      detailVisible: false,
      detailLoading: false,
      detail: null,
      // product popup
      productPopupVisible: false,
      productPopupData: null,
      // import
      importVisible: false,
      importStep: 1,
      importFile: null,
      validating: false,
      confirming: false,
      importForm: { company_id: null, branch_code: DEFAULT_BRANCH },
      batch: { id: null, row_total: 0, row_ok: 0, row_error: 0 },
      previewRows: [],
      // manual add
      addVisible: false,
      saving: false,
      addForm: { company_id: null, branch_code: DEFAULT_BRANCH, sold_at: null, items: [] },
    };
  },
  mounted() {
    this.loadCompanies();
    this.loadList();
  },
  beforeDestroy() {
    this.cancelPendingBatch();
  },
  methods: {
    // ── shared helpers ──────────────────────────────────────────────────────
    extractPayload(res) {
      const rd = (res && res.data) || {};
      if (rd.Data && rd.Data.length) return rd.Data[0] || {};
      if (rd.Datum !== undefined && rd.Datum !== null) return rd.Datum;
      return rd;
    },
    errMsg(err) {
      return (err && err.response && err.response.data && err.response.data.Cause) || (err && err.message) || "Error";
    },
    sourceLabel(source) {
      if (source === "manual") return this.$t("modern_trade.sales_entry.source_manual");
      return this.$t("modern_trade.sales_entry.source_import");
    },
    sourceBadgeClass(source) {
      // theme colours — badge-secondary was too washed out to read (#155)
      return source === "manual" ? "badge-info" : "badge-primary";
    },
    formatDate(v, withTime) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = (n) => String(n).padStart(2, "0");
      const day = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      if (!withTime) return day;
      return `${day} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    // Preview cells: never render an empty cell. qty === 0 (bad_qty rows) is a
    // real value and must stay visible, so only null/undefined/"" become "-".
    cellText(v) {
      if (v === null || v === undefined || v === "") return "-";
      return v;
    },
    lotCellText(row) {
      if (row.lot_no) return row.lot_no;
      // Error rows were never matched against stock → "auto (FEFO)" would be a lie.
      return this.previewErrorMode ? "-" : this.$t("modern_trade.sales_entry.lot_auto_fefo");
    },
    translateError(msg) {
      const map = {
        unknown_tax_id: this.$t("modern_trade.validation.unknown_tax_id"),
        company_inactive: this.$t("modern_trade.validation.company_inactive"),
        // #155: sales import matches by SKU/barcode only — wording must not
        // mention product code any more.
        unknown_product: this.$t("modern_trade.sales_entry.err_unknown_product"),
        missing_sku_or_barcode: this.$t("modern_trade.sales_entry.err_missing_sku_or_barcode"),
        bad_date: this.$t("modern_trade.validation.bad_date"),
        bad_qty: this.$t("modern_trade.validation.bad_qty"),
        duplicate_row: this.$t("modern_trade.validation.duplicate_row"),
        future_sale_date: this.$t("modern_trade.validation.future_sale_date"),
        unknown_lot: this.$t("modern_trade.validation.unknown_lot"),
        insufficient_stock: this.$t("modern_trade.validation.insufficient_stock"),
      };
      return map[msg] || msg;
    },

    // ── list ────────────────────────────────────────────────────────────────
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = this.extractPayload(res);
        this.companies = d.items || d.data || [];
      } catch (e) {
        // non-fatal
      }
    },
    onFilter() {
      this.page = 1;
      this.loadList();
    },
    async loadList() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.filters.company_id) params.company_id = this.filters.company_id;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        if (this.filters.search) params.search = this.filters.search;
        const res = await ModernTradeService.salesEntriesList(params);
        const d = this.extractPayload(res);
        this.rows = d.data || d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },

    // ── detail ──────────────────────────────────────────────────────────────
    async openDetail(row) {
      this.detailVisible = true;
      this.detailLoading = true;
      this.detail = null;
      try {
        const res = await ModernTradeService.salesEntryGet(row.id);
        this.detail = this.extractPayload(res);
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
        this.detailVisible = false;
      } finally {
        this.detailLoading = false;
      }
    },
    showProduct(item) {
      // #200: tolerate both the legacy sku_code/barcode spelling and the
      // resolved-WMS wms_man_pn/wms_barcode spelling.
      this.productPopupData = {
        product_name: item.product_name,
        wms_name: item.wms_name,
        wms_man_pn: item.wms_man_pn,
        sku_code: item.sku_code,
        wms_barcode: item.wms_barcode,
        barcode: item.barcode,
        wms_linked: item.wms_linked,
        wms_product_id: item.wms_product_id,
      };
      this.productPopupVisible = true;
    },

    // ── import modal ────────────────────────────────────────────────────────
    openImport() {
      this.importForm = { company_id: null, branch_code: DEFAULT_BRANCH };
      this.importStep = 1;
      this.importFile = null;
      this.batch = { id: null, row_total: 0, row_ok: 0, row_error: 0 };
      this.previewRows = [];
      this.importVisible = true;
    },
    onImportClosed() {
      // release any uncommitted validated batch
      this.cancelPendingBatch();
      this.importStep = 1;
      this.importFile = null;
      this.previewRows = [];
      if (this.$refs.importFileInput) this.$refs.importFileInput.value = "";
    },
    onFileChange(e) {
      this.importFile = e.target.files[0] || null;
    },
    async doValidate() {
      if (!this.importFile || !this.importForm.company_id) return;
      this.validating = true;
      try {
        const fd = new FormData();
        fd.append("file", this.importFile);
        fd.append("company_id", this.importForm.company_id);
        fd.append("branch_code", this.importForm.branch_code || DEFAULT_BRANCH);
        const res = await ModernTradeService.salesImportValidate(fd);
        const d = this.extractPayload(res);
        this.batch = {
          id: d.batch_id,
          row_total: d.row_total || 0,
          row_ok: d.row_ok || 0,
          row_error: d.row_error || 0,
        };
        this.previewRows = d.rows || [];
        this.importStep = 2;
      } catch (err) {
        // #157 HIGH-1: on validation_failed (400) BE still returns the
        // per-row breakdown in Data[0].errors — surface it in the same
        // step-2 preview table instead of a single opaque toast.
        const d = (err.response && err.response.data && err.response.data.Data && err.response.data.Data[0]) || null;
        if (d && d.errors) {
          this.batch = {
            id: null,
            row_total: d.row_total || 0,
            row_ok: d.row_ok || 0,
            row_error: d.row_error || 0,
          };
          // bo-api#… error row: { row_no, sku_code, barcode, lot_no, error }
          // + sale_date / qty (added round-13). Spell them out with a fallback
          // so an older BE that omits them renders "-" instead of blank cells.
          this.previewRows = d.errors.map((e) => ({
            ...e,
            sale_date: e.sale_date !== undefined && e.sale_date !== null ? e.sale_date : "",
            qty: e.qty !== undefined && e.qty !== null ? e.qty : "",
            status: 2,
            error_msg: e.error,
          }));
          this.importStep = 2;
        } else {
          swal.fire({ title: this.errMsg(err), icon: "error" });
        }
      } finally {
        this.validating = false;
      }
    },
    backToImportStep1() {
      // discard the validated batch and go back to file picker
      this.cancelPendingBatch();
      this.importStep = 1;
      this.batch = { id: null, row_total: 0, row_ok: 0, row_error: 0 };
      this.previewRows = [];
    },
    async doConfirm() {
      if (this.batch.row_error > 0 || !this.batch.id) return;
      this.confirming = true;
      try {
        await ModernTradeService.salesImportConfirm({
          batch_id: this.batch.id,
          company_id: this.importForm.company_id,
          branch_code: this.importForm.branch_code || DEFAULT_BRANCH,
        });
        this.batch.id = null; // committed — guards won't cancel it
        this.importVisible = false;
        swal.fire({
          title: this.$t("modern_trade.sales_entry.import_success"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.loadList();
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.confirming = false;
      }
    },
    cancelPendingBatch() {
      const bid = this.batch && this.batch.id;
      if (!bid) return;
      this.batch.id = null;
      ModernTradeService.batchCancel(bid).catch(() => {});
    },
    downloadTemplate() {
      // #155 / bo-api#306: sku_code + barcode only (at least one per row).
      const rows = [this.templateHeaders, ...TEMPLATE_SAMPLE_ROWS];
      const ws = utils.aoa_to_sheet(rows);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "sales_import");
      writeFile(wb, "sales_import_template.xlsx");
    },

    // ── manual add modal ──────────────────────────────────────────────────────
    openAdd() {
      this.addForm = { company_id: null, branch_code: DEFAULT_BRANCH, sold_at: null, items: [] };
      this.addItem();
      this.addVisible = true;
    },
    onAddClosed() {
      this.addForm = { company_id: null, branch_code: DEFAULT_BRANCH, sold_at: null, items: [] };
    },
    newItem() {
      return {
        _key: Date.now() + Math.random(),
        product_id: null,
        product: null,
        productOptions: [],
        productLoading: false,
        lot_no: "",
        lotOptions: [],
        lotLoading: false,
        qty: 1,
      };
    },
    addItem() {
      this.addForm.items.push(this.newItem());
    },
    removeItem(index) {
      this.addForm.items.splice(index, 1);
    },
    productLabel(p) {
      const sku = p.man_pn || p.sku_code || "";
      return sku ? `${p.name} (${sku})` : p.name;
    },
    lotLabel(lot) {
      const exp = lot.expire_date ? this.formatDate(lot.expire_date, false) : "-";
      return `${lot.lot_no} (${this.$t("modern_trade.sales_entry.lot_remaining")} ${lot.qty_remaining}, ${this.$t("modern_trade.sales_entry.lot_expire")} ${exp})`;
    },
    async searchProducts(query, row) {
      row.productLoading = true;
      try {
        const res = await ModernTradeService.wmsProductOptions({ search: query || "", per_page: 50 });
        const d = this.extractPayload(res);
        row.productOptions = d.items || d.data || [];
      } catch (e) {
        row.productOptions = [];
      } finally {
        row.productLoading = false;
      }
    },
    async onProductChange(row) {
      row.product = (row.productOptions || []).find((p) => p.id === row.product_id) || null;
      row.lot_no = "";
      row.lotOptions = [];
      if (!row.product_id) return;
      row.lotLoading = true;
      try {
        const res = await ModernTradeService.lotOptions({ wms_product_id: row.product_id });
        const d = this.extractPayload(res);
        row.lotOptions = d.data || d.items || [];
      } catch (e) {
        row.lotOptions = [];
      } finally {
        row.lotLoading = false;
      }
    },
    async doSaveManual() {
      if (!this.addForm.company_id) {
        swal.fire({ title: this.$t("modern_trade.sales_entry.company_required"), icon: "warning" });
        return;
      }
      if (!this.addForm.sold_at) {
        swal.fire({ title: this.$t("modern_trade.sales_entry.sold_at_required"), icon: "warning" });
        return;
      }
      const validItems = this.addForm.items.filter((it) => it.product && it.product_id);
      if (!validItems.length) {
        swal.fire({ title: this.$t("modern_trade.sales_entry.add_item_required"), icon: "warning" });
        return;
      }
      if (validItems.some((it) => !it.qty || it.qty < 1)) {
        swal.fire({ title: this.$t("modern_trade.sales_entry.qty_required"), icon: "warning" });
        return;
      }
      this.saving = true;
      try {
        const payload = {
          company_id: this.addForm.company_id,
          branch_code: this.addForm.branch_code || DEFAULT_BRANCH,
          sold_at: this.addForm.sold_at,
          items: validItems.map((it) => ({
            sku_code: it.product.man_pn || it.product.sku_code || "",
            // #157 MEDIUM-3: also send barcode so products without man_pn
            // still resolve on BE (tries sku_code, then falls back to barcode).
            barcode: it.product.barcode || "",
            lot_no: it.lot_no || undefined,
            qty: it.qty,
          })),
        };
        await ModernTradeService.salesEntryCreate(payload);
        this.addVisible = false;
        swal.fire({
          title: this.$t("modern_trade.sales_entry.save_success"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.loadList();
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* #155: "แหล่งที่มา" badge was unreadable at the default table font size */
.source-badge {
  font-size: 0.78rem;
  padding: 0.4rem 0.7rem;
  letter-spacing: 0;
  white-space: nowrap;
}
.format-hint {
  padding-left: 1.1rem;
  margin-bottom: 0;
  font-size: 0.82rem;
  color: #525f7f;
}
.format-hint li {
  margin-bottom: 0.15rem;
}
.format-sample {
  font-size: 0.78rem;
}
.format-sample th {
  white-space: nowrap;
}
</style>
